import React from "react";

import { faCode } from "@fortawesome/pro-regular-svg-icons";

import { Card } from "src/base-components/Card";
import { EmptyState } from "src/design-system/EmptyState";
import { DashboardContent } from "src/layout/DashboardContent";
import { SdkScriptModalShell } from "src/settings/sdks/components/SdkScriptModalShell";
import { SdkScriptsTable } from "src/settings/sdks/components/SdkScriptsTable";
import { SdkSettingsSubHeader } from "src/settings/sdks/components/SdkSettingsSubHeader";
import { useSdkSettingsSearchParams } from "src/settings/sdks/hooks/useSdkSettingsSearchParams";
import { MOCK_SDK_SCRIPTS } from "src/settings/sdks/mocks/sdkScripts";
import { SdkSettingsTab } from "src/settings/sdks/searchParams";
import { SdkScriptRow } from "src/settings/sdks/types";
import { filterSdkScriptRows } from "src/settings/sdks/utils/filterRows";

const getModalTitle = (
  isCreateOpen: boolean,
  scriptToEdit: SdkScriptRow | undefined,
): string => {
  if (isCreateOpen) {
    return "Create SDK script";
  }
  if (scriptToEdit) {
    return `Edit ${scriptToEdit.name}`;
  }
  return "SDK script";
};

export const SdkScriptsPage: React.FC = () => {
  const {
    searchQuery,
    setSearch,
    entityId,
    isCreateOpen,
    isEditOpen,
    openCreateModal,
    openEditModal,
    closeModal,
  } = useSdkSettingsSearchParams(SdkSettingsTab.Scripts);

  const filteredRows = filterSdkScriptRows(MOCK_SDK_SCRIPTS, searchQuery);
  const scriptToEdit = entityId
    ? MOCK_SDK_SCRIPTS.find((row) => row.id === entityId)
    : undefined;
  // Derive open state from the URL intent alone so a stale `edit` id (no
  // matching row) still renders a dismissible modal that can clear the params.
  const isModalOpen = isCreateOpen || isEditOpen;

  return (
    <DashboardContent
      Header={
        <SdkSettingsSubHeader
          addDataLoc="sdk-scripts-add-button"
          addTooltip="Add SDK script"
          searchDataLoc="sdk-scripts-search"
          searchQuery={searchQuery}
          onAddClick={openCreateModal}
          onSearchChange={setSearch}
        />
      }
    >
      <Card
        className="flex h-full min-h-0 flex-col pt-1"
        dataLoc="sdk-scripts-card"
        fullWidth
        variant="inverted"
      >
        <Card.Content className="h-full min-h-0 flex-1">
          {filteredRows.length === 0 ? (
            <EmptyState
              dataLoc="sdk-scripts-empty-state"
              description={
                searchQuery
                  ? "Try adjusting your search query"
                  : "Add SDK scripts to use in bundles"
              }
              headline={
                searchQuery ? "No matching SDK scripts" : "No SDK scripts yet"
              }
              icon={faCode}
            />
          ) : (
            <SdkScriptsTable
              rows={filteredRows}
              onRowClick={(row) => openEditModal(row.id)}
            />
          )}
        </Card.Content>
      </Card>

      <SdkScriptModalShell
        open={isModalOpen}
        title={getModalTitle(isCreateOpen, scriptToEdit)}
        onClose={closeModal}
      />
    </DashboardContent>
  );
};
